import useWalletIsConnected from '../../../hooks/useWalletIsConnected';
import useUserAccountIsReady from '../../../hooks/useUserAccountIsReady';
import useVelocityAccountStore from 'src/stores/useVelocityAccountsStore';
import { Health } from '@velocity-exchange/icons';
import useIsMobileScreenSize from '../../../hooks/useIsMobileScreenSize';
import { useEffect, useMemo } from 'react';
import useVelocityStore from 'src/stores/VelocityStore/useVelocityStore';
import { TabOption } from 'src/components/primitives/Tabs/types';
import healthDependentClassName from 'src/utils/healthDependentClassName';

export type TradeTableTabValue =
	| 'balances'
	| 'account'
	| 'positions'
	| 'openOrders'
	| 'tradeHistory'
	| 'orderHistory'
	| 'positionHistory'
	| 'unsettled';

interface Params {
	balanceCount: number;
	positionCount: number;
	openOrderCount: number;
	overviewPage?: boolean;
	activeTab: TradeTableTabValue;
}

export const useTradePageTableTabs = ({
	balanceCount,
	positionCount,
	openOrderCount,
	overviewPage,
	activeTab,
}: Params) => {
	const isMobileScreenSize = useIsMobileScreenSize();

	const tabOptions = useMemo(() => {
		const options: TabOption<TradeTableTabValue>[] = (() => {
			if (overviewPage) {
				return [
					{
						value: 'positions',
						label: renderWithCount('Positions', positionCount),
						screenSize: 'all',
					},
					{
						value: 'openOrders',
						label: renderWithCount('Orders', openOrderCount),
						screenSize: 'all',
					},
					{
						value: 'orderHistory',
						label: 'Order History',
						screenSize: 'all',
					},
					{
						value: 'positionHistory',
						label: 'Position History',
						screenSize: 'all',
					},
				];
			}

			return [
				{
					value: 'positions',
					label: renderWithCount('Positions', positionCount),
					screenSize: 'all',
				},
				{
					value: 'openOrders',
					label: renderWithCount('Orders', openOrderCount),
					screenSize: 'all',
				},
				{
					value: 'balances',
					label: renderWithCount('Balances', balanceCount),
					screenSize: 'all',
				},
				{
					value: 'orderHistory',
					label: 'Order History',
					screenSize: 'all',
				},
				{
					value: 'positionHistory',
					label: 'Position History',
					screenSize: 'all',
				},
			];
		})();

		const accountTabOption: TabOption<TradeTableTabValue> = {
			value: 'account',
			label: <AccountTabLabel />,
			screenSize: 'all',
		};

		if (!overviewPage && isMobileScreenSize) {
			options.unshift(accountTabOption);
		}

		return options;
	}, [
		activeTab,
		overviewPage,
		positionCount,
		openOrderCount,
		balanceCount,
		isMobileScreenSize,
	]);

	useHandleInvalidTab(tabOptions);

	return tabOptions;
};

const useHandleInvalidTab = (options: { value: TradeTableTabValue }[]) => {
	const activeTab = useVelocityStore((s) => s.tradePageTable.currentTab);
	const setState = useVelocityStore((s) => s.set);

	useEffect(() => {
		if (!options.some((option) => option.value === activeTab)) {
			setState((s) => {
				s.tradePageTable.currentTab = options[0].value;
			});
		}
	}, [activeTab, setState, options]);
};

function renderWithCount(label: string, count: number) {
	if (!count) {
		return label;
	}

	return `${label} (${count})`;
}

const AccountTabLabel = () => {
	const connected = useWalletIsConnected();

	const userAccountIsReady = useUserAccountIsReady();
	const userClient = useVelocityAccountStore((s) => {
		return s.accounts[s.currentUserKey ?? '']?.client;
	});

	const health =
		connected && userAccountIsReady && userClient
			? userClient.getHealth()
			: 100;

	return (
		<div className="flex items-center gap-x-1">
			<span>Account</span>
			<Health
				size={14}
				color={healthDependentClassName(health, {
					type: 'color-var',
				})}
			/>
		</div>
	);
};
